import { FC } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { routes } from 'commons/constants/routes'
import { Icon, IconFace, IconName, IconSize } from 'ui/components/Icon'
import { Info, InfoSet, InfoSetDirection } from 'ui/components/Info'
import { UsPhoneLabel } from 'ui/components/Labels'
import { Link } from 'ui/components/Link'

import { COMPANY_DETAILS_KEY, useSetCompanyOwners } from '../../../../../../hooks'
import { useCompanyOwners } from '../CompanyOwners'
import { RelationShipInformationProps } from './RelationshipInformationProps'

import { Column, Columns, CompanyOwnersRow, IconInfo } from './styles'

export const RelationShipInformation: FC<RelationShipInformationProps> = (props) => {
  const {
    data: {
      id,
      companyName,
      legalName,
      dba,
      isHolding,
      subsidiaries,
      customerStatus,
      applicationId,
      relationshipOwners,
      availableOwners,
      phone,
      website,
      tags,
      parentHoldingId,
      parentHoldingName,
      showSubApplications,
      internalNotes
    }
  } = props
  const queryClient = useQueryClient()
  const { onSetCompanyOwners } = useSetCompanyOwners(() =>
    queryClient.invalidateQueries({ queryKey: [COMPANY_DETAILS_KEY] })
  )

  const holding = parentHoldingId ? { id: parentHoldingId, name: parentHoldingName || '' } : undefined

  /*
    Merging availableOwners and assignedBankUsers, because for select we should have all available options for
    correct work.
   */
  const fullOptions = availableOwners.concat(
    ...relationshipOwners.map((el) => {
      const mappedEl = {
        fullName: el.name,
        id: el.id
      }
      if (availableOwners.find((el) => el.id === mappedEl.id) !== undefined) {
        return []
      }
      return [mappedEl]
    })
  )

  const { CompanyOwnersDialog, openCompanyOwners } = useCompanyOwners(
    id,
    relationshipOwners.map((i) => i.id) || [],
    relationshipOwners.map((i) => i.name).join(',') || 'Add user',
    fullOptions,
    onSetCompanyOwners
  )

  const hasSubsidiaries = !!subsidiaries?.length

  // MP-5531:2 the Holding company has subsidiaries but do not belong to another Holding company
  const showIsHolding = !!holding && hasSubsidiaries

  return (
    <InfoSet legend="Relationship Details" direction={InfoSetDirection.ROW}>
      <Columns>
        <Column>
          <Info label="Company Name">{companyName}</Info>
          <Info label="Entity Legal Name">{legalName}</Info>
          <Info label="DBA">{dba}</Info>
          {showIsHolding && (
            <IconInfo label="Is Holding">
              <Icon
                face={isHolding ? IconFace.ACTIVE : IconFace.DEFAULT}
                size={IconSize.S}
                name={isHolding ? IconName.CHECK : IconName.CLEAR}
              />
            </IconInfo>
          )}
          <Info label="Subsidiaries">
            {hasSubsidiaries ? (
              subsidiaries.map((company) => (
                <div key={company.id}>
                  <Link to={routes.bank.relationships.details} params={{ id: company.id.toString() }}>
                    {company.name}
                  </Link>
                </div>
              ))
            ) : (
              <>---</>
            )}
          </Info>

          <Info label="Parent Holding">
            {holding ? (
              <Link to={routes.bank.relationships.details} params={{ id: holding.id.toString() }}>
                {holding.name}
              </Link>
            ) : (
              <>---</>
            )}
          </Info>
          <Info label="Customer Status">{customerStatus}</Info>
          <Info label="Applications">
            {(!!applicationId || showSubApplications !== undefined) && (
              <>
                {!!applicationId && (
                  <div>
                    <Link
                      to="/onboarding/applications/$applicationId"
                      params={{ applicationId: applicationId.toString() }}
                    >
                      {companyName}
                    </Link>
                  </div>
                )}
                {showSubApplications !== undefined &&
                  showSubApplications.map((el, key) => (
                    <div key={key}>
                      <Link to="/onboarding/applications/$applicationId" params={{ applicationId: el.id.toString() }}>
                        {el.name}
                      </Link>
                    </div>
                  ))}
              </>
            )}
          </Info>
        </Column>
        <Column>
          <Info label="Relationship Owner(s)">
            <CompanyOwnersRow>
              <CompanyOwnersDialog />
              {relationshipOwners.map((item) => item.name).join(', ')}
              <Icon name={IconName.EDIT} size={IconSize.XS} onClick={openCompanyOwners} />
            </CompanyOwnersRow>
          </Info>
          <Info label="Phone">
            <UsPhoneLabel value={phone} />
          </Info>
          <Info label="Website">{website}</Info>
          <Info label="Tags">{tags.join(', ')}</Info>
        </Column>
      </Columns>
      <Info label="Notes" dataQA="internalNotes">
        {internalNotes}
      </Info>
    </InfoSet>
  )
}
